/**
 * Turn an answer option into a React Hook Form field-name suffix.
 *
 * Detail boxes under an option ("tell us more") are stored as their own
 * field, and the field name is built from the option text. Spaces have
 * always been turned into underscores, and that name is what previously
 * saved answers are stored under.
 *
 * One combination cannot be used as a field name. React Hook Form treats
 * a comma as a break in the name, then strips apostrophes on the way in
 * and keeps them on the way out. The name it writes and the name it reads
 * back are different, so the textarea's ref crashes with
 * "Cannot read properties of undefined (reading '_f')". The Reason for
 * Referral option "Yes, but I'm not sure where it was from" is that case:
 * the box is mounted as soon as the question appears, so the form crashes
 * on open, before anything is saved.
 *
 * Only that combination is rewritten (every run of punctuation becomes one
 * underscore). Every other option keeps the historical space-to-underscore
 * name, so detail text already saved in production still loads.
 *
 * @param {string} option - The option label, e.g. "Yes, but I'm not sure where it was from"
 * @returns {string} A field-name suffix React Hook Form can register
 */
export function sanitizeOptionForKey(option) {
  const raw = String(option);
  const legacy = raw.replace(/\s+/g, '_');
  if (legacy.includes(',') && legacy.includes("'")) {
    return raw.replace(/\W+/g, '_');
  }
  return legacy;
}

/**
 * Full React Hook Form field name for an option's detail box.
 * Save, reload, and the textarea must all use this, or the typed text
 * is stored under a different name from the one the box reads.
 *
 * @param {string} answerKey - The question's field name
 * @param {string} option - The option label
 * @returns {string}
 */
export function otherOptionFieldKey(answerKey, option) {
  return `${answerKey}_other_${sanitizeOptionForKey(option)}`;
}
